import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { MallCategory, MallItem } from "../api/types";
import { UploadButton } from "../components/ui/UploadButton";

/**
 * Round 30 point 2 — Mall item catalog (migration 0058). coins_price is
 * the ONLY source of truth for what an item costs — buy_mall_item()
 * (called from backend-worker/src/routes/coins.js's POST /coins/mall)
 * looks this up itself, server-side; Android never sends or trusts a
 * price. "Purchasable OFF" (is_purchasable=false) means the item still
 * shows in the app (so users can see it's coming) but Buy is disabled —
 * and buy_mall_item() independently refuses the purchase either way, so
 * this is never just a client-side suggestion.
 *
 * No real asset upload exists in this Worker yet — same as Gifts/Room
 * Reactions, the PNG/SVGA fields here are pasted URLs. If/when a real
 * upload endpoint gets built for one catalog, wiring it in here is the
 * same one-line change as it'd be for those.
 */
export function MallItemsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<MallItem>("/admin/mall-items");
  const [categories, setCategories] = useState<MallCategory[]>([]);
  const [editing, setEditing] = useState<Partial<MallItem> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    apiRequest<MallCategory[]>("/admin/mall-categories").then(setCategories).catch(() => {});
  }, []);

  function categoryName(id: number | null) {
    if (!id) return "—";
    return categories.find((c) => c.id === id)?.name ?? `#${id}`;
  }

  async function save() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    if (editing.coins_price === undefined || editing.coins_price === null || Number(editing.coins_price) < 0) {
      showToast("Coin price must be zero or more", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        category_id: editing.category_id || null,
        name: editing.name,
        png_asset_url: editing.png_asset_url || null,
        svga_asset_url: editing.svga_asset_url || null,
        coins_price: Number(editing.coins_price),
        duration_days: editing.duration_days ? Number(editing.duration_days) : null,
        is_purchasable: editing.is_purchasable ?? true,
        is_active: editing.is_active ?? true,
        sort_order: Number(editing.sort_order) || 0,
        slot_type: editing.slot_type || null,
      };
      if (editing.id) {
        await apiRequest(`/admin/mall-items/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/mall-items", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save item", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: MallItem) {
    try {
      await apiRequest(`/admin/mall-items/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update item", "error");
    }
  }

  async function togglePurchasable(row: MallItem) {
    try {
      await apiRequest(`/admin/mall-items/${row.id}`, { method: "PATCH", body: { is_purchasable: !row.is_purchasable } });
      showToast(row.is_purchasable ? "Purchasing turned off" : "Purchasing turned on", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update item", "error");
    }
  }

  const columns: Column<MallItem>[] = [
    {
      header: "Item",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.png_asset_url ? (
            <img src={row.png_asset_url} className="h-10 w-10 rounded-lg object-cover" alt="" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-surface-muted" />
          )}
          <div>
            <p className="font-medium text-ink">{row.name}</p>
            <p className="text-xs text-ink-faint">{categoryName(row.category_id)}</p>
          </div>
        </div>
      ),
    },
    { header: "Price", render: (row) => `${row.coins_price} coins` },
    { header: "Duration", render: (row) => (row.duration_days ? `${row.duration_days} days` : "Permanent") },
    {
      // Round 30 point 2/3 — which Bag equip slot this item fills. Null
      // means it's a display-only/Bag-only item with no equip behavior.
      header: "Equip Slot",
      render: (row) => (row.slot_type ? <Badge tone="gold">{row.slot_type}</Badge> : <span className="text-xs text-ink-faint">—</span>),
    },
    {
      header: "Purchasable",
      render: (row) => (row.is_purchasable ? <Badge tone="success">On</Badge> : <Badge tone="warn">Off</Badge>),
    },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>Edit</Button>
          <Button size="sm" variant="secondary" onClick={() => togglePurchasable(row)}>
            {row.is_purchasable ? "Turn Off Buy" : "Turn On Buy"}
          </Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Mall Items">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        This is the real catalog the Android app's Mall tab reads from — nothing here is hardcoded in the app, and
        coins_price is looked up server-side on every purchase, never trusted from Android. "Purchasable Off"
        keeps an item visible (so users can see it's coming) without letting anyone actually buy it yet; there's no
        delete here on purpose, so a real purchase a user already made never loses its catalog reference —
        deactivate instead.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ is_active: true, is_purchasable: true, sort_order: 0, coins_price: 0 })}>
          + Add Item
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No Mall items yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Item" : "Add Item"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} isLoading={isSubmitting}>Save</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Select
            label="Category"
            value={editing?.category_id ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, category_id: e.target.value ? Number(e.target.value) : null }))}
          >
            <option value="">No category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
          <Input
            label="PNG asset URL"
            value={editing?.png_asset_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, png_asset_url: e.target.value }))}
          />
          {editing?.slot_type === "chat_bubble" && (
            <p className="-mt-2 text-xs text-ink-faint">Chat bubble: the file is stored exactly as uploaded (PNG/WebP, max 2 MB) so a 9-patch image keeps its black stretch/text-area border. Without a 9-patch border the app cuts it automatically (middle half stretches).</p>
          )}
          <UploadButton kind={editing?.slot_type === "chat_bubble" ? "chat_bubble" : "mall_item"} onUploaded={(u) => setEditing((p) => ({ ...p, png_asset_url: u }))} />
          <Input
            label="SVGA asset URL (optional)"
            value={editing?.svga_asset_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, svga_asset_url: e.target.value }))}
          />
          <Input
            label="Coin price"
            type="number"
            value={editing?.coins_price ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, coins_price: Number(e.target.value) }))}
          />
          <Select
            label="Duration"
            value={editing?.duration_days ? String(editing.duration_days) : "permanent"}
            onChange={(e) => setEditing((p) => ({ ...p, duration_days: e.target.value === "permanent" ? null : Number(e.target.value) }))}
          >
            <option value="permanent">Permanent</option>
            <option value="1">1 day</option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
          </Select>
          <Select
            label="Purchasable"
            value={editing?.is_purchasable === false ? "off" : "on"}
            onChange={(e) => setEditing((p) => ({ ...p, is_purchasable: e.target.value === "on" }))}
          >
            <option value="on">On — users can buy this</option>
            <option value="off">Off — visible, not buyable yet</option>
          </Select>
          <Select
            label="Equip Slot (Round 30 Bag/equip system)"
            value={editing?.slot_type ?? ""}
            onChange={(e) =>
              setEditing((p) => ({ ...p, slot_type: (e.target.value || null) as MallItem["slot_type"] }))
            }
          >
            <option value="">Not equippable (display/Bag-only item)</option>
            <option value="frame">Frame — shown around the avatar when seated/on profile</option>
            <option value="car">Car — plays an SVGA entrance effect on room join</option>
            <option value="chat_bubble">Chat Bubble</option>
          </Select>
        </div>
      </Modal>
    </AppShell>
  );
}
